import { create } from "zustand";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import { fetchPatternStats, type FocusCohort, type PatternFocus, type PatternStatsResponse } from "./patterns.api";

interface PatternsState {
  ownerUserId: string | null;
  statsByScope: Record<string, PatternStatsResponse | undefined>;
  loadingByScope: Record<string, boolean | undefined>;
  errorsByScope: Record<string, string | null | undefined>;
  setOwner: (userId: string | null) => void;
  invalidateEvidence: () => void;
  loadPatterns: (cohort?: FocusCohort, exactTimeControl?: string) => Promise<void>;
  refreshPatterns: (cohort?: FocusCohort, exactTimeControl?: string) => Promise<void>;
  setFocus: (cohort: FocusCohort, exactTimeControl: string | undefined, focus: PatternFocus) => void;
}

export const patternQueryKey = (cohort: FocusCohort, exactTimeControl?: string): string =>
  `${cohort}:${exactTimeControl ?? "all"}@${useUserSettingsStore.getState().settings.patternsDefaultPeriod}`;

const inFlight = new Map<string, Promise<void>>();
const latest = new Map<FocusCohort, string>();
let ownerRevision = 0;

const withoutCohort = <T>(values: Record<string, T>, cohort: FocusCohort): Record<string, T> =>
  Object.fromEntries(Object.entries(values).filter(([key]) => !key.startsWith(`${cohort}:`)));

const requestPatterns = (
  set: (update: (state: PatternsState) => Partial<PatternsState>) => void,
  cohort: FocusCohort,
  exactTimeControl?: string,
  preserve = false,
) => {
  const key = patternQueryKey(cohort, exactTimeControl);
  const revision = ownerRevision;
  const requestKey = `${revision}:${key}`;
  if (!preserve) latest.set(cohort, key);
  const pending = inFlight.get(requestKey);
  if (pending) return pending;
  set((state) => ({
    statsByScope: preserve ? state.statsByScope : { ...state.statsByScope, [key]: undefined },
    loadingByScope: { ...withoutCohort(state.loadingByScope, cohort), [key]: true },
    errorsByScope: { ...withoutCohort(state.errorsByScope, cohort), [key]: null },
  }));
  const request = fetchPatternStats(
    cohort,
    exactTimeControl,
    useUserSettingsStore.getState().settings.patternsDefaultPeriod,
  )
    .then((stats) => {
      if (revision !== ownerRevision || (!preserve && latest.get(cohort) !== key)) return;
      set((state) => ({
        statsByScope: { ...state.statsByScope, [key]: stats },
        loadingByScope: { ...state.loadingByScope, [key]: false },
      }));
    })
    .catch((error: unknown) => {
      if (revision === ownerRevision && (preserve || latest.get(cohort) === key))
        set((state) => ({
          loadingByScope: { ...state.loadingByScope, [key]: false },
          errorsByScope: { ...state.errorsByScope, [key]: "Failed to load patterns — try again later" },
        }));
      throw error;
    })
    .finally(() => inFlight.delete(requestKey));
  inFlight.set(requestKey, request);
  return request;
};

export const usePatternsStore = create<PatternsState>((set, get) => ({
  ownerUserId: null,
  statsByScope: {},
  loadingByScope: {},
  errorsByScope: {},
  invalidateEvidence: () => {
    ownerRevision += 1;
    latest.clear();
    set({ statsByScope: {}, loadingByScope: {}, errorsByScope: {} });
  },
  setOwner: (ownerUserId) => {
    if (get().ownerUserId === ownerUserId) return;
    ownerRevision += 1;
    latest.clear();
    set({ ownerUserId, statsByScope: {}, loadingByScope: {}, errorsByScope: {} });
  },
  loadPatterns: async (cohort = "rapid", exactTimeControl) => {
    const key = patternQueryKey(cohort, exactTimeControl);
    if (get().statsByScope[key]) return;
    return requestPatterns(set, cohort, exactTimeControl);
  },
  refreshPatterns: async (cohort = "rapid", exactTimeControl) => {
    if (exactTimeControl !== undefined) return requestPatterns(set, cohort, exactTimeControl, true);
    const prefix = `${cohort}:`;
    const exactClocks = Object.keys(get().statsByScope)
      .filter(
        (key) =>
          key.startsWith(prefix) &&
          key.endsWith(`@${useUserSettingsStore.getState().settings.patternsDefaultPeriod}`) &&
          key !== patternQueryKey(cohort),
      )
      .map((key) => key.slice(prefix.length).split("@")[0]);
    await requestPatterns(set, cohort, undefined, true);
    for (const clock of exactClocks) await requestPatterns(set, cohort, clock, true);
  },
  setFocus: (cohort, exactTimeControl, focus) => {
    const key = patternQueryKey(cohort, exactTimeControl);
    set((state) => ({
      statsByScope: {
        ...state.statsByScope,
        [key]: state.statsByScope[key] ? { ...state.statsByScope[key], focus } : undefined,
      },
    }));
  },
}));
